import React from "react";
import { Tooltip } from "@heroui/react";
import { Typography } from "../../../../components";
import { OnlyFansAccount } from "../../../../api/staff";
import { InfoCircle } from "../../../../components/icons";

interface AccountCellProps {
  account: OnlyFansAccount;
}

/**
 * AccountCell Component
 *
 * Renders the account information cell including:
 * - Avatar
 * - Account name
 * - OnlyFans username with link
 * - Status chips (Free/Paid, popularity %, status)
 *
 * Based on onlyu_front design
 */
const AccountCell: React.FC<AccountCellProps> = ({ account }) => {
  const ofData = account?.of_data || {};

  // Tooltip content for additional info
  const renderTooltipContent = () => {
    const parseNumber = (value: unknown) => {
      if (value === null || value === undefined) return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    };

    const formatCurrency = (value: unknown) => `$${parseNumber(value).toFixed(2)}`;
    const formatInteger = (value: unknown) => parseNumber(value).toLocaleString();

    const balance = formatCurrency(ofData?.balance);
    const subscribers = formatInteger(ofData?.subscribers ?? ofData?.subscribers_count);
    const favorited = formatInteger(ofData?.favorited ?? ofData?.likes_count);
    const posts = formatInteger(ofData?.posts ?? ofData?.posts_count);
    const blockedCountriesCount = Array.isArray(ofData?.blocked_countries)
      ? ofData.blocked_countries.length
      : 0;
    const blockedCountries = blockedCountriesCount.toLocaleString();

    return (
      <div className="bg-white border border-[#EDEDED] rounded-[12px] p-0 min-w-[280px]">
        {/* Header */}
        <div className="px-[16px] pt-[16px] pb-0">
          <Typography variant="body2" className="text-[#11181C] text-[14px] font-normal leading-[18px]">
            Общая информация
          </Typography>
        </div>

        {/* Stats */}
        <div className="flex flex-col gap-[4px] px-[16px] py-0">
          {/* Balance */}
          <div className="flex items-center justify-between py-[6px]">
            <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
              Баланс
            </Typography>
            <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
              {balance}
            </Typography>
          </div>

          {/* Subscribers */}
          <div className="flex items-center justify-between py-[6px]">
            <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
              Подписчики
            </Typography>
            <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
              {subscribers}
            </Typography>
          </div>

          {/* Favorited */}
          <div className="flex items-center justify-between py-[6px]">
            <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
              Лайки
            </Typography>
            <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
              {favorited}
            </Typography>
          </div>

          {/* Posts */}
          <div className="flex items-center justify-between py-[6px]">
            <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
              Посты
            </Typography>
            <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
              {posts}
            </Typography>
          </div>

          {/* Blocked countries */}
          <div className="flex items-center justify-between py-[6px]">
            <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
              Заблокированные страны
            </Typography>
            <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
              {blockedCountries}
            </Typography>
          </div>
        </div>

        {/* Footer */}
        <div className="px-[16px] pb-[16px]">
          <Typography variant="body4" className="text-[#888B8D] text-[10px] font-normal leading-[16px]">
            {ofData?.updated_at ? `Обновлено: ${new Date(ofData.updated_at).toLocaleString('ru-RU')}` : 'Нет данных об обновлении'}
          </Typography>
        </div>
      </div>
    );
  };

  const overallPopularityPercent = (() => {
    const value = ofData?.overall_popularity_percent;
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  })();

  return (
    <div className="flex items-start gap-[12px]">
      {/* Avatar */}
      <div className="min-w-[40px] mt-[3px] max-h-[40px] min-h-[40px] max-w-[40px]">
        <img
          src={account.avatar || '/default-avatar.png'}
          alt={account.name}
          className="min-w-[40px] min-h-[40px] max-w-[40px] max-h-[40px] rounded-[12px] object-cover"
        />
      </div>

      {/* Details */}
      <div className="flex flex-col gap-[4px]">
        {/* Name and Username Link */}
        <div className="flex items-center gap-[8px] flex-wrap">
          <Typography variant="label2" className="whitespace-nowrap text-[#11181C] font-medium">
            {account.name}
          </Typography>
          <div className="flex items-center gap-[2px] flex-nowrap">
            <a
              target="_blank"
              href={`https://onlyfans.com/${account.of_user_name}`}
              rel="noopener noreferrer"
              className="text-[#006FEE] hover:underline flex items-center gap-1 whitespace-nowrap"
            >
              <Typography variant="body2" className="!text-[#006FEE]">
                @{account.of_user_name}
              </Typography>
            </a>
            <Tooltip
              content={renderTooltipContent()}
              placement="bottom"
              showArrow={false}
              classNames={{
                content: "p-0 bg-transparent shadow-none",
              }}
            >
              <div className="cursor-pointer">
                <InfoCircle />
              </div>
            </Tooltip>
          </div>
        </div>

        {/* Status Chips */}
        <div className="flex flex-wrap gap-[8px] mt-[4px]">
          {/* Type Chip */}
          <span className={`inline-flex h-[24px] items-center justify-center rounded-lg bg-[#006FEE]/20 px-[8px] text-[12px] font-medium text-[#006FEE] ${account?.type === 'free' ? 'uppercase' : 'capitalize'}`}>
            {account?.type}
          </span>

          {/* Popularity Chip */}
          <span className="inline-flex h-[24px] items-center justify-center rounded-lg bg-[#F5A524]/20 px-[8px] text-[12px] font-medium text-[#F5A524]">
            {overallPopularityPercent}%
          </span>

          {/* Status Chip */}
          <span className={`inline-flex h-[24px] items-center justify-center rounded-lg px-[8px] text-[12px] font-medium ${
            account.status === 'active'
              ? 'bg-[#17C964]/20 text-[#17C964]'
              : account.status === 'moderation'
              ? 'bg-[#7828C8]/20 text-[#7828C8]'
              : 'bg-[#F31260]/20 text-[#F31260]'
          }`}>
            {account.status === 'active'
              ? 'Доступ активен'
              : account.status === 'moderation'
              ? 'Модерация'
              : 'Нет доступа'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccountCell;
